<template>
    <section class="sb-section">
        <hr v-if="divider" class="division" />

        <!-- Header: toggles collapse when >1 item, navigates directly when ==1 -->
        <button class="sb-section__header" type="button" @click="onHeaderClick"
            :aria-expanded="(!hasSingle && isOpen) ? 'true' : 'false'"
            :aria-controls="!hasSingle ? sectionBodyId : null">
            <span class="material-symbols-outlined sb-section__icon">{{ icon }}</span>
            <span class="sb-section__title">{{ title }}</span>
            <span v-if="!hasSingle" class="material-symbols-outlined sb-section__chevron"
                :class="{ open: isOpen }">expand_more</span>
        </button>



        <!-- Only show the nav when there are >= 2 items -->
        <transition name="sb-collapse">
            <nav v-show="!collapsible || isOpen" v-if="visibleItems.length > 1" class="sb-section__body"
                :id="sectionBodyId">
                <ul class="sb-list">
                    <li v-for="(it, idx) in visibleItems" :key="idx" class="sb-list__item"
                        :class="{ activeRoute: isActive(it.to) }">
                        <router-link v-if="it.to" :to="it.to" class="sb-link" exact @click.native="onNavigate">
                            <span class="material-symbols-outlined sb-link__icon" v-if="it.icon">{{ it.icon }}</span>
                            <span class="sb-link__label">{{ renderLabel(it) }}</span>
                        </router-link>

                        <a v-else-if="it.href" :href="it.href" class="sb-link" @click="onNavigate">
                            <span class="material-symbols-outlined sb-link__icon" v-if="it.icon">{{ it.icon }}</span>
                            <span class="sb-link__label">{{ renderLabel(it) }}</span>
                        </a>

                        <span v-else class="sb-link sb-link--disabled">
                            <span class="material-symbols-outlined sb-link__icon" v-if="it.icon">{{ it.icon }}</span>
                            <span class="sb-link__label">{{ renderLabel(it) }}</span>
                        </span>
                    </li>
                </ul>
            </nav>
        </transition>
    </section>
</template>

<script>
export default {
    name: 'SidebarSection',
    props: {
        title: { type: String, required: true },
        icon: { type: String, default: '' },
        items: { type: Array, default: () => [] }, // [{ to|href, labelKey|label, icon?, visible? }]
        collapsible: { type: Boolean, default: true },
        startOpen: { type: Boolean, default: false },
        divider: { type: Boolean, default: false },
        dense: { type: Boolean, default: true },
    },
    data() {
        return { isOpen: this.startOpen };
    },
    computed: {
        visibleItems() {
            return this.items.filter(it => it.visible !== false);
        },
        hasSingle() {
            return this.visibleItems.length === 1;
        },
        singleItem() {
            return this.hasSingle ? this.visibleItems[0] : null;
        },
        sectionBodyId() {
            // a small id for aria-controls when collapsible
            return `sb-body-${this._uid}`;
        },
    },
    methods: {
        toggle() { this.isOpen = !this.isOpen; },
        onNavigate() { this.$emit('navigate'); },
        renderLabel(it) {
            if (it.label) return it.label;
            if (it.labelKey) return this.$i18n.t(it.labelKey);
            return '';
        },
        isActive(to) {
            if (!to || !this.$route) return false;

            if (typeof to === "string") {
                return this.$route.path === to || this.$route.path.startsWith(to);
            }

            if (to.name) {
                return this.$route.name === to.name;
            }

            return false;
        },
        onHeaderClick() {
            // If there is exactly one item, navigate to it instead of toggling
            if (this.hasSingle && this.singleItem) {
                console.log(this.singleItem)
                this.navigateTo(this.singleItem);
                return;
            }
            // Otherwise, normal collapse toggle
            this.toggle();
        },
        navigateTo(it) {
            if (!it) return;
            if (it.to && this.$router) {
                this.$router.push(it.to).catch(() => { });
                this.onNavigate();
            } else if (it.href) {
                // Use location.assign to keep default browser behavior
                window.location.assign(it.href);
                this.onNavigate();
            }
        },
    },
};
</script>

<style scoped>
/* =========================
     SidebarSection.vue styles
     ========================= */

/* --- Divider: same look as your old <hr class="division"> --- */
.division {
    width: 200px;
    height: 1px;
    margin: 10px 0;
    background: rgba(255, 255, 255, 0.25);
    border: 0;
}


/* Root section */
.sb-section {
    width: 100%;
}

/* --- Header (collapsible + static) --- */
.sb-section__header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0;
    /* matches your .topic header padding */
    background: transparent;
    color: #e6edf3;
    border: 0;
    cursor: pointer;
    text-align: left;
    border-radius: 0.25rem;
    width: 100%;
    user-select: none;
}

.sb-section__header:hover {
    background: rgba(255, 255, 255, 0.06);
}

.sb-section__header--static {
    cursor: default;
}

/* Icon column alignment like old list */
.sb-section__icon,
.sb-link__icon {
    font-size: 18px;
    line-height: 1;
    vertical-align: middle;
    opacity: 0.9;
    min-width: 1.25rem;
    /* keeps a neat icon column */
    text-align: center;
}

/* Title (topic): match your weight/size */
.sb-section__title {
    font-weight: 700;
    /* bolder */
    font-size: 1.125rem;
    /* ≈ your .topic a */
    letter-spacing: 0.2px;
}

/* Chevron rotation */
.sb-section__chevron {
    margin-left: auto;
    transition: transform 0.2s ease;
}

.sb-section__chevron.open {
    transform: rotate(180deg);
}

/* Body indentation: same as your #collapse-* { margin-left: 1rem } */
.sb-section__body {
    padding-left: 1rem;
}

/* List + items */
.sb-list {
    list-style: none;
    padding: 0.25rem 0 0.5rem;
    margin: 0;
}

.sb-list__item {
    margin: 0;
}

/* Links: mirror your .nav-item a rhythm */
.sb-link {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.35rem 0;
    /* like your .nav-item a */
    color: aliceblue;
    text-decoration: none;
    border-radius: 0.25rem;
    font-size: 1rem;
    opacity: 0.75;
    transition: opacity 0.4s ease, color 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: auto;

}

.sb-link__label {
    width: 10rem;
    /* or max-width if you want it to shrink/grow */
    white-space: normal;
    /* allow wrapping */
    word-break: break-word;
    /* break long words if needed */
    overflow-wrap: anywhere;
    /* modern replacement */
}

.sb-link:hover {
    opacity: 1;
    color: #ffffff;
}

.sb-link--disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Active highlight: same as your .activeRoute a */
.activeRoute>.sb-link {
    color: #e0900f;
    text-shadow: 0 0 6px rgba(224, 144, 15, 1);
}

/* Collapse transition speed to feel like original */
.sb-collapse-enter-active,
.sb-collapse-leave-active {
    transition: height 0.18s ease, opacity 0.18s ease;
}

.sb-collapse-enter,
.sb-collapse-leave-to {
    height: 0;
    opacity: 0;
    overflow: hidden;
}

/* --- Density helpers (optional) --- */
.dense .sb-section__header {
    padding: 0.4rem 0;
}

.dense .sb-link {
    padding: 0.3rem 0;
}

.comfortable .sb-section__header {
    padding: 0.6rem 0;
}

.comfortable .sb-link {
    padding: 0.45rem 0;
}
</style>